import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  
  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      faqs: [
        {
          id: 'q1',
          question: 'What is the Digital Approval of Building Designs platform?',
          answer: 'The Digital Approval of Building Designs platform is an online system that streamlines the process of submitting and obtaining approvals for building designs. It allows users to upload PDF drawings and 3D IFC models, which are then reviewed by authorized approvers through a multi-level approval process.'
        },
        {
          id: 'q2',
          question: 'Who can use this platform?',
          answer: 'This platform can be used by architects, engineers, building owners, contractors, and any professional involved in building design and construction who needs to obtain official approval for their building designs.'
        },
        {
          id: 'q3',
          question: 'Is the platform available 24/7?',
          answer: 'Yes, the platform is available 24/7 for submissions and viewing project status. However, the review and approval processes are conducted during regular business hours (Monday to Friday, 8:00 AM - 5:00 PM).'
        },
        {
          id: 'q4',
          question: 'How secure is my data on this platform?',
          answer: 'The platform implements industry-standard security measures, including encrypted data transmission, secure authentication, and regular security audits. Your designs and personal information are protected with the highest level of security protocols.'
        },
      ]
    },
    {
      id: 'submission',
      title: 'Submission Process',
      faqs: [
        {
          id: 'q5',
          question: 'What file formats are accepted for submission?',
          answer: 'The platform accepts PDF files for drawings and IFC format files for 3D models. PDFs should be properly formatted according to our standards, and IFC files should be compatible with IFC.js for proper visualization.'
        },
        {
          id: 'q6',
          question: 'Is there a limit to the file size I can upload?',
          answer: 'Yes, individual PDF files should not exceed 50MB, and IFC model files should not exceed 100MB. If your files are larger, please contact support for assistance with large file submissions.'
        },
        {
          id: 'q7',
          question: 'Can I submit multiple designs at once?',
          answer: 'You can create multiple projects in your account, but each project submission is processed individually. This ensures that each design receives proper attention and review.'
        },
        {
          id: 'q8',
          question: 'What if I need to update my submission after it\'s been uploaded?',
          answer: 'You can update your submission only if it has not yet been processed by an Approver Level 1. Once the approval process has begun, you cannot modify the files unless the project is returned to you with a request for changes.'
        },
      ]
    },
    {
      id: 'approval',
      title: 'Approval Process',
      faqs: [
        {
          id: 'q9',
          question: 'How long does the approval process take?',
          answer: 'The typical approval process takes 10-15 business days, depending on the complexity of the design and the current volume of submissions. Each level of approval (1, 2, and 3) may take 3-5 business days.'
        },
        {
          id: 'q10',
          question: 'What happens if my design is rejected?',
          answer: 'If your design is rejected, you will receive detailed feedback explaining the reasons for rejection. You can then make the necessary modifications and resubmit your design. Note that resubmissions may incur additional fees depending on the extent of changes required.'
        },
        {
          id: 'q11',
          question: 'Can I track the status of my submission?',
          answer: 'Yes, you can track the status of your submission in real-time through your account dashboard. The platform provides updates at each stage of the approval process.'
        },
        {
          id: 'q12',
          question: 'Who reviews my building designs?',
          answer: 'Your designs are reviewed by qualified professionals with expertise in architecture, engineering, and building code compliance. The platform employs a three-level approval process, with each level focusing on different aspects of the design.'
        },
      ]
    },
    {
      id: 'payment',
      title: 'Fees and Payment',
      faqs: [
        {
          id: 'q13',
          question: 'What are the fees for using the platform?',
          answer: 'Fees vary based on the building type, location, and complexity. Basic submission fees start at $500, with additional fees for extra files or resubmissions. The exact fee structure will be displayed before you complete your submission.'
        },
        {
          id: 'q14',
          question: 'What payment methods are accepted?',
          answer: 'The platform accepts credit/debit cards (Visa, MasterCard, American Express), bank transfers, and electronic checks. All payments are processed securely through our payment gateway.'
        },
        {
          id: 'q15',
          question: 'When do I need to pay for my submission?',
          answer: 'Payment is required at the time of submission. Your project will not enter the approval queue until payment has been successfully processed.'
        },
        {
          id: 'q16',
          question: 'Are there any refunds if my design is rejected?',
          answer: 'Submission fees are non-refundable, as they cover the cost of the review process regardless of the outcome. However, we offer a discounted resubmission fee if you need to make changes and resubmit.'
        },
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      faqs: [
        {
          id: 'q17',
          question: 'What browsers are supported by the platform?',
          answer: 'The platform supports the latest versions of Chrome, Firefox, Safari, and Edge. For the best experience, especially when viewing 3D models, we recommend using Chrome or Firefox.'
        },
        {
          id: 'q18',
          question: 'What do I do if I encounter an error during submission?',
          answer: 'If you encounter any errors, first try refreshing the page or using a different browser. If the issue persists, please contact our technical support team at support@buildingapprovals.gov or call (555) 123-4567.'
        },
        {
          id: 'q19',
          question: 'Can I use the platform on mobile devices?',
          answer: 'Yes, the platform is responsive and can be accessed on mobile devices. However, for optimal experience when uploading files or viewing 3D models, we recommend using a desktop or laptop computer.'
        },
        {
          id: 'q20',
          question: 'Do I need any special software to view my 3D models?',
          answer: 'No, the platform has a built-in IFC.js viewer that allows you to view and interact with your 3D models directly in your browser. No additional software is required.'
        },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Header Banner */}
      <section className="bg-blue-700 py-24 bglog">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl font-bold text-white text-center">Frequently Asked Questions</h1>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto relative">
            <input
              type="text"
              placeholder="Search FAQs..."
              className="w-full p-4 pl-12 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
            <Search className="absolute left-4 top-4 text-gray-400" size={20} />
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 container mx-auto px-6">
        {faqCategories.map((category, categoryIndex) => (
          <div key={category.id} className="mb-16">
            <h2 className="text-2xl font-bold mb-8 pb-2 border-b-2 border-gray-200">{category.title}</h2>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {category.faqs.map((faq, faqIndex) => {
                const index = `${categoryIndex}-${faqIndex}`;
                const isActive = activeIndex === index;
                
                return (
                  <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      className={`w-full flex items-center justify-between p-4 text-left font-medium ${isActive ? 'bg-blue-50' : 'bg-white'}`}
                      onClick={() => toggleAccordion(index)}
                    >
                      <span className="text-lg">{faq.question}</span>
                      {isActive ? (
                        <ChevronUp className="text-blue-700" size={20} />
                      ) : (
                        <ChevronDown className="text-gray-400" size={20} />
                      )}
                    </button>
                    
                    {isActive && (
                      <div className="p-4 bg-white border-t border-gray-200">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            If you couldn't find the answer to your question, please contact our support team.
            We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="/contact"
              className="bg-blue-700 text-white py-3 px-8 rounded-md font-medium hover:bg-blue-800 transition duration-300"
            >
              Contact Support
            </a>
            <a
              href="tel:+15551234567"
              className="text-blue-700 font-medium hover:text-blue-800 transition duration-300"
            >
              Call (555) 123-4567
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;